import type { WhiteLabelTheme } from '@huddle-sports-platform/schema';

/**
 * The white-label build contract.
 *
 * A club's branded app is THIS codebase with one difference: the flavour
 * pipeline stamps `WHITE_LABEL` with the club's identity at build time, and
 * everything locked-club follows from it - the theme applies before first
 * paint, the doors wear the club's crest and name, and the profile form
 * skips create/join to land straight on the club's membership path.
 *
 * The SHARED Huddle app ships with `null` here. On the web, the same locked
 * mode also engages at runtime when someone arrives via
 * `app.huddlesports.ie/{slug}` - `ClubBrandStore` resolves the slug through
 * `lookupClubBySlug` and stores the same shape, so every downstream
 * behaviour has exactly one source of truth.
 */
export interface WhiteLabelConfig {
  clubId: string;
  slug: string;
  /** Display name - the doors' wordmark. */
  appName: string;
  /** The club's FULL registered name and its location, from the club doc.
   *  Optional: the stamped/lookup first paint carries only the wordmark;
   *  ClubBrandStore's live listener fills these in once the admin is
   *  signed in and keeps them current. */
  name?: string;
  location?: string;
  /** The club's sport ('basketball') - seeds sport-aware defaults like the
   *  squad builder's ladder. Optional: older stamps predate it. */
  sport?: string;
  logoUrl: string | null;
  primaryColour: string;
  secondaryColour: string | null;
  /** The RESOLVED palettes from the club's manifest - applied, never derived. */
  theme: WhiteLabelTheme;
}

/**
 * Stamped per flavour build by the white-label pipeline; null = Huddle.
 *
 * COMMITTED AS NULL, always. `wl:reset` is a `git checkout --` of this file
 * and the native identity files, so whatever is committed here IS the
 * "shared Huddle app" that reset claims to restore. A stamp that gets
 * committed turns every later reset into a club build - and takes the
 * gitignored `public/club-crest.png` with it, so the app then 404s the very
 * crest it is pointing at. Stamp locally, never commit the stamp.
 */
export const WHITE_LABEL: WhiteLabelConfig | null = {
  "clubId": "7BNF7",
  "slug": "dublinraiders",
  "appName": "Raiders",
  "sport": "basketball",
  "logoUrl": "/club-crest.png",
  "primaryColour": "#4f8ac9",
  "secondaryColour": "#122a4d",
  "theme": {
    "light": {
      "accent": "#0B2545",
      "accentHover": "#103664",
      "accentSoft": "rgba(11, 37, 69, 0.1)",
      "accentContrast": "#F5F2EC",
      "accentHoverContrast": "#F5F2EC"
    },
    "dark": {
      "accent": "#96C0EE",
      "accentHover": "#B5D2F3",
      "accentSoft": "rgba(150, 192, 238, 0.14)",
      "accentContrast": "#131419",
      "accentHoverContrast": "#131419",
      "bg": "#00000A",
      "panel": "#15151E",
      "panelRaised": "#242432"
    }
  }
};
